import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  FileDownload as DownloadIcon,
  ContactPhone as ContactsIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { familyGroupService } from '../../services/familyGroupService';
import ContactCard from './ContactCard';
import {
  getFamilyContacts,
  filterContactsBySearch,
  filterContactsByGroup,
  downloadCSV
} from '../../services/contactService';
import logger from '../../utils/logger';

/**
 * Contacts Page
 * Displays family member contacts with search, filter, and export capabilities
 */
const Contacts = () => {
  const { currentUser } = useAuth();

  // State
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'card'

  // Check if user is admin
  const isAdmin = currentUser?.isAdmin || currentUser?.isSuperAdmin;

  /**
   * Load all family contacts
   */
  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.familyCode) {
        setError('No family code found. Please complete your profile.');
        return;
      }

      const fetchedContacts = await getFamilyContacts(
        currentUser.familyCode,
        currentUser.uid,
        isAdmin
      );

      setContacts(fetchedContacts);
      setFilteredContacts(fetchedContacts);
    } catch (err) {
      logger.error('Error loading contacts:', err);
      setError('Failed to load contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin]);

  /**
   * Load groups for filtering (NESTED STRUCTURE)
   */
  const loadGroups = useCallback(async () => {
    try {
      if (!currentUser?.familyCode) return;

      // Use familyGroupService to get groups from nested structure
      const familyGroups = await familyGroupService.getFamilyGroups(currentUser.familyCode);

      const groupsList = familyGroups.map(group => ({
        id: group.id,
        name: group.name,
        type: group.type
      }));

      // Sort by name
      groupsList.sort((a, b) => a.name.localeCompare(b.name));

      setGroups(groupsList);
    } catch (err) {
      logger.error('Error loading groups:', err);
    }
  }, [currentUser]);

  /**
   * Apply search and group filters
   */
  const applyFilters = useCallback(() => {
    let filtered = [...contacts];

    // Apply search filter
    filtered = filterContactsBySearch(filtered, searchTerm);

    // Apply group filter
    filtered = filterContactsByGroup(filtered, selectedGroup);

    setFilteredContacts(filtered);
  }, [contacts, searchTerm, selectedGroup]);

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
    loadGroups();
  }, [loadContacts, loadGroups]);

  // Apply filters when search term or group changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  /**
   * Handle export to CSV
   */
  const handleExport = () => {
    try {
      downloadCSV(filteredContacts, 'family-contacts.csv');
    } catch (err) {
      logger.error('Error exporting contacts:', err);
      setError('Failed to export contacts. Please try again.');
    }
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  /**
   * Handle group filter change
   */
  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ContactsIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Family Contacts
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and connect with your family members. {isAdmin && '(Admin view - you can see all contact information)'}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters — compact on mobile */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={8} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Group"
                onChange={handleGroupChange}
              >
                <MenuItem value="all">All Groups</MenuItem>
                {groups.map(group => (
                  <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} sm={2} md={2}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={filteredContacts.length === 0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Count + View Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredContacts.length} of {contacts.length} contacts
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="list"><Tooltip title="List view"><ViewListIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="card"><Tooltip title="Card view"><GridViewIcon fontSize="small" /></Tooltip></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Contact list or cards */}
      {filteredContacts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ContactsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchTerm || selectedGroup !== 'all'
              ? 'No contacts found matching your search'
              : 'No contacts available'}
          </Typography>
          {(searchTerm || selectedGroup !== 'all') && (
            <Button
              sx={{ mt: 2 }}
              onClick={() => { setSearchTerm(''); setSelectedGroup('all'); }}
            >
              Clear Filters
            </Button>
          )}
        </Paper>
      ) : viewMode === 'list' ? (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <List disablePadding>
            {filteredContacts.map((contact, idx) => (
              <React.Fragment key={contact.id}>
                <ListItemButton sx={{ py: 0.75, px: 2 }}>
                  <ListItemAvatar>
                    <Avatar
                      src={contact.photoURL}
                      sx={{ width: 36, height: 36, fontSize: '0.85rem' }}
                    >
                      {contact.displayName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600}>
                        {contact.displayName}
                        {contact.isCurrentUser && ' (You)'}
                      </Typography>
                    }
                    secondary={
                      [contact.relationship, contact.email].filter(Boolean).join(' · ') || null
                    }
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItemButton>
                {idx < filteredContacts.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }} />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredContacts.map(contact => (
            <Grid item xs={12} sm={6} md={4} key={contact.id}>
              <ContactCard contact={contact} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Privacy Notice */}
      {!isAdmin && (
        <Box sx={{ mt: 4 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Privacy Notice:</strong> Family members control what contact information is visible to others.
              If you don't see someone's phone or address, they have chosen to keep it private.
              You can manage your own privacy settings in your Profile.
            </Typography>
          </Alert>
        </Box>
      )}
    </Container>
  );
};

export default Contacts;
